import clsx from "clsx";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import React, { useId } from "react";

export type PillTabItem = {
  label?: string;
  value?: string;
  icon?: LucideIcon;
  count?: number;
  dot?: boolean;
  title?: string;
};

type PillTabsProps = {
  tabs: (string | PillTabItem)[];
  activeTab: string;
  onTabChange: (tabValue: string) => void;
  className?: string;
  /**
   * `"contained"` (default) — compact selector on a muted bg, white pill for active.
   * `"section"` — full-width section nav with dark pill (bg-foreground) for active,
   *   border-b separator, and smooth framer-motion sliding indicator.
   */
  variant?: "contained" | "section";
};

const SPRING = { type: "spring" as const, stiffness: 380, damping: 30 };

const PillTabs: React.FC<PillTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className,
  variant = "contained",
}) => {
  const uid = useId();

  if (variant === "section") {
    return (
      <div
        className={clsx(
          "flex shrink-0 items-center gap-1 overflow-x-auto border-b border-border bg-card/40 px-4 py-1.5",
          "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
          className,
        )}
      >
        {tabs.map((item) => {
          const isObj = typeof item === "object" && item !== null;
          const label = isObj ? (item as PillTabItem).label : (item as string);
          const value = isObj
            ? ((item as PillTabItem).value ?? (item as PillTabItem).label)
            : (item as string);
          const Icon = isObj ? (item as PillTabItem).icon : undefined;
          const count = isObj ? (item as PillTabItem).count : undefined;
          const dot = isObj ? (item as PillTabItem).dot : undefined;
          const active = activeTab === value;

          return (
            <button
              key={value}
              type="button"
              onClick={() => onTabChange(value)}
              className={clsx(
                "relative z-0 inline-flex h-7 items-center gap-1.5 rounded-md px-2.5",
                "text-[12px] font-medium whitespace-nowrap transition-colors outline-none",
                active
                  ? "text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {/* Animated dark pill indicator — slides between tabs */}
              {active && (
                <motion.div
                  layoutId={`pill-section-${uid}`}
                  className="absolute inset-0 z-[-1] rounded-md bg-foreground"
                  transition={SPRING}
                />
              )}
              {/* Hover bg for inactive tabs — behind the button text */}
              {!active && (
                <span className="absolute inset-0 z-[-1] rounded-md opacity-0 group-hover:opacity-100 bg-muted transition-opacity" />
              )}
              {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
              {label}
              {typeof count === "number" && (
                <span
                  className={clsx(
                    "grid h-4 min-w-4 place-items-center rounded px-1 text-[10px] font-medium tabular-nums",
                    active
                      ? "bg-background/20 text-background"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {count}
                </span>
              )}
              {dot && (
                <span className="ml-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-success" />
              )}
            </button>
          );
        })}
      </div>
    );
  }

  // "contained" variant — original style
  return (
    <div
      className={clsx(
        "inline-flex items-center p-1 bg-muted/80 rounded-md",
        className,
      )}
    >
      {tabs.map((item) => {
        const isObj = typeof item === "object" && item !== null;
        const label = isObj ? (item as PillTabItem).label : (item as string);
        const value = isObj
          ? ((item as PillTabItem).value ?? (item as PillTabItem).label)
          : (item as string);
        const Icon = isObj ? (item as PillTabItem).icon : undefined;
        const title = isObj ? (item as PillTabItem).title : undefined;

        const active = activeTab === value;

        return (
          <button
            key={value}
            type="button"
            onClick={() => onTabChange(value!)}
            title={title || label}
            className={clsx(
              "relative z-0 flex items-center justify-center gap-1.5 px-2 py-1 text-[11.5px] font-medium rounded-md transition-colors whitespace-nowrap outline-none cursor-pointer",
              label ? "flex-1" : "shrink-0 px-2.5",
              active
                ? "text-foreground font-semibold"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {active && (
              <motion.div
                layoutId={`pill-indicator-${uid}`}
                className="absolute inset-0 z-[-1] bg-background rounded-md shadow-sm"
                transition={SPRING}
              />
            )}
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default PillTabs;
